import React from "react";
import { gql } from "graphql-tag";
import { useParams } from "react-router-dom";
import { Card, CardHeader } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { updateFormDefinition, updateFormDefinitionVariables, updateFormDefinitionForm } from "types/gql-types";
import { LoadingSpinner } from "components/graphql-views";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import DefinitionsForm, { FormDefinition, defaultFormSchema } from "./form";
import { saveFormDefinitionUpdate } from "./form-definition-actions";

const GET_DEFINITION = gql`
  query updateFormDefinitionForm($id: ID!) {
    formDefinition(id: $id) {
      id
      name
      content
      enabled
      createInterest
      propertyId
      formType
      skipSignature
      renderParagraphs
      ageRequirement
      ageRequirementOrDisabilityOverride
      formId
      form {
        id
        open
        display
      }
    }
  }
`;

const UPDATE_DEFINITION = gql`
  mutation updateFormDefinition($id: ID!, $data: FormDefinitionInput!) {
    updateFormDefinition(id: $id, data: $data) {
      id
      name
      content
      enabled
      createInterest
      propertyId
      formType
      skipSignature
      renderParagraphs
      ageRequirement
      ageRequirementOrDisabilityOverride
      formId
    }
  }
`;

// eslint-disable-next-line max-lines-per-function -- query + submit + render in one place
const UpdateFormDefinition: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { openSnackbar } = useSnackbar();
  const { loading, error, data } = useQuery<updateFormDefinitionForm>(GET_DEFINITION, {
    fetchPolicy: "cache-and-network",
    variables: { id },
  });
  const [updateForm] = useMutation<
  updateFormDefinition,
  updateFormDefinitionVariables
  >(UPDATE_DEFINITION);

  if (!id) return null;

  const submitForm = (formData: FormDefinition) => saveFormDefinitionUpdate({
    id,
    formData,
    updateForm,
    openSnackbar,
    logError: (err) => logger.sendError(err),
    cast: (draft) => defaultFormSchema.cast({ ...draft }) as unknown as FormDefinition,
  });

  if (error) <span>Server Error</span>;
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  const formDefinition =
    defaultFormSchema.cast({ ...data.formDefinition }) as unknown as FormDefinition;

  formDefinition.id = id;

  return (
    <Card>
      <CardHeader title="Update a Form Draft" subheader="Required fields marked with *" />
      <DefinitionsForm
        formType="update"
        initialData={formDefinition}
        handleSubmit={submitForm}
      />
    </Card>
  );
};

export default UpdateFormDefinition;
